import React, { useState, useEffect, useRef } from 'react';
import { NumberWithStaticUnitsInput, Tooltip } from 'components/common';

// A number and its unit entered in ONE row, for the case where the unit is a short fixed list rather
// than a value the user types: the count and the unit that count is measured in.
//
// The units are whatever `unit_values` lists. Nothing here is specific to any of them:
//
//   form_type: delayed_integer_with_static_units
//   unit_name: wastage_unit                              # sibling attribute the choice is written to
//   unit_values: [sheet, product]                        # ANY values -- [mm, in] works the same way
//   unit_label: quote_builder.wastage_unit               # optional, names the picker for screen readers
//   unit_translate_prefix: 'quote_builder.wastage_unit_' # optional; absent means the values ARE the labels
//   unit_publish_item_key: quote_builder.…_changed       # optional, published when the unit changes
//
// The unit itself lives in the sibling attribute named by `unit_name`, which is what the form posts;
// this field only decides where it is entered. `delayed_integer_with_unit` is untouched and still
// renders the unit as a read-only label taken from another attribute.
// The automatic material fire adeti re-broadcasts this column's total several times while the cost sections
// re-price on load (each section's response carries it), and the total can step up as contributors come online.
// Apply only the LATEST report after this quiet window, so the input shows the settled total rather than flickering
// through each intermediate step. A genuine re-price loop is spaced by server round-trips far longer than this, so
// its reports still land as separate applications and the cycle guard still stops them.
const RECOMPUTE_SETTLE_DEBOUNCE_MS = 300;

const DelayedNumberWithStaticUnitsFormField = ({ config, value, values, setValue, register, errors, readOnly, disabled, integerOnly, requestPublishItemOnChange, navSection, item }) => {
  const unit = values[config.unit_name];

  // This material column's UNIQUE key (item uuid + extra_item_reason) -- the column_key Frank::MaterialAutoWastage
  // emits. NOT the display title: a carton_lamination section's base and extra columns share the "Lamination" title
  // (rule 7), so the title cannot tell them apart; UserUnitPrice matches its reported wastage the same way.
  const columnKey = item != undefined ? `${item.uuid}_${item.extra_item_reason || ''}` : undefined;

  // The material fire adeti automatic entry for THIS material column (auto_breakdown fields only): its computed
  // total and the per-contributor breakdown that fills the badge row. Seeded from window.material_auto_wastages at
  // page load and refreshed by the material_wastage_recomputed event. Matched by nav section label AND column_key
  // so each column reads its OWN value -- a double printing block's carton and carton_extra, and a
  // carton_lamination base and extra, do not share one. undefined for the nav-section wastage field.
  const materialAutoEntry = () => {
    if(!config.auto_breakdown || !navSection) { return undefined; }
    const list = (typeof window !== 'undefined' && Array.isArray(window.material_auto_wastages)) ? window.material_auto_wastages : [];
    return list.find((entry) => entry.label === navSection.label && (entry.column_key || '') === (columnKey || ''));
  };
  // When config.auto_field names a boolean sibling (the section's wastage_count_auto), this field carries an
  // inline auto/manual toggle in place of a separate checkbox row: automatic shows the computed value read-only
  // with a tooltip and an Edit button that hands control back to the user; manual shows the input with a button
  // to return to automatic. Frank (version 2) only -- the automatic computation lives in the Frank solve. Read
  // before the value seed below so an automatic material field can paint its settled value on the FIRST render.
  const hasAutoToggle = config.auto_field != undefined &&
    typeof window !== 'undefined' && window.user_quote && window.user_quote.version == 2;
  const isAuto = hasAutoToggle && Boolean(values[config.auto_field]);

  // An already-automatic material fire adeti field seeds its FIRST paint from the server-computed
  // window.material_auto_wastages (materialAutoEntry) -- the SAME value the mount effect below syncs into the form.
  // Its default_value_eval carries the section-level wastage_count (windowNavSection().wastage_count), a DIFFERENT
  // number (the nav-section auto total, not this material column's), so seeding from `value` would paint that and
  // then visibly correct to the material total once the mount effect runs -- the "34 -> 0" flicker. auto_breakdown
  // only; every other field (and manual mode) keeps the plain `value` seed.
  const [inputValue, setInputValue] = useState(() => {
    const autoEntry = (config.auto_breakdown && isAuto) ? materialAutoEntry() : undefined;
    return (autoEntry && autoEntry.total != undefined) ? autoEntry.total : value;
  });
  const [breakdown, setBreakdown] = useState(() => (materialAutoEntry() || {}).breakdown || []);

  // The field's current value, kept in a ref so the recompute listener (whose effect does not re-subscribe on every
  // keystroke) can compare the response's authoritative total against the LATEST value, not the one captured when it
  // subscribed. Used only to decide whether a recompute is a real change worth re-pricing for.
  const currentValueRef = useRef(inputValue);
  useEffect(() => { currentValueRef.current = inputValue; }, [inputValue]);

  // The loop guard for the self-correct below: the set of automatic totals this field has already re-priced against
  // since it last converged, plus a once-per-cycle report flag. When the server keeps alternating between totals the
  // field has already pushed (a non-converging ping-pong), re-posting forever is stopped -- see the recompute listener.
  const pushedAutoValuesRef = useRef(new Set());
  const cycleReportedRef = useRef(false);

  // Pending debounced recompute application (RECOMPUTE_SETTLE_DEBOUNCE_MS), so a burst of reports paints once.
  const recomputeTimerRef = useRef(null);

  // A field-level read-only expression evaluated against the current form `values` (e.g. the wastage
  // count turns read-only while "otomatik fire" is ticked). Re-evaluated on every render, so it tracks
  // the field it depends on the same way visible_if does -- but the scope here is narrower than
  // visible_if's: only `values` and `window` are in scope (not navSection/options/item), so a
  // readonly_if expression must confine itself to those. When a parent passes a section-wide `readOnly`
  // it still forces read-only (the v1 FormFieldFactory does; the Frank/v2 form does not pass one).
  const configReadOnly = config.readonly_if != undefined ? Boolean(eval(config.readonly_if)) : false;
  const effectiveReadOnly = readOnly || configReadOnly;

  // A corrugated section that follows a carton_lamination leader (mirroring Frank::LinkedCorrugatedWastage) does
  // not pick its own machines: its automatic fire adeti comes from the leader's Kesim (die-cutting) item only
  // (rule 8), so it gets a tooltip that says so. The navSection prop is a slim nav-builder shape that
  // does not carry the link, so the leader id is read off window.nav_sections -- the source windowNavSection
  // resolves the full section from. It matches by label only (windowNavSection tries index first, then label);
  // label is enough here because a quote's nav section labels are unique. No match / no leader -> generic tooltip.
  const linkedLeaderId = (() => {
    if (!navSection) { return undefined; }
    const sections = (typeof window !== 'undefined' && Array.isArray(window.nav_sections)) ? window.nav_sections : [];
    const resolved = sections.find((section) => section.label === navSection.label);
    return resolved && resolved.solution_user_quote_nav_section_id;
  })();
  const isLinkedCorrugatedFollower = Boolean(
    navSection &&
    ['corrugated_cardboard_offset', 'corrugated_cardboard'].includes(navSection.part_type) &&
    linkedLeaderId
  );
  const autoTooltip = (isLinkedCorrugatedFollower && config.auto_tooltip_linked_corrugated != undefined)
    ? config.auto_tooltip_linked_corrugated
    : config.auto_tooltip;

  const setAuto = (nextAuto) => {
    setValue(config.auto_field, nextAuto);
    // Toggling auto/manual starts a fresh automatic session, so reset the self-correct loop guard.
    pushedAutoValuesRef.current.clear();
    cycleReportedRef.current = false;
    // A field with an automatic breakdown (material's fire adeti) fills in its current computed total + breakdown
    // the moment it switches to automatic, rather than flashing the manual value until the re-price event lands.
    if(nextAuto && config.auto_breakdown) {
      const entry = materialAutoEntry();
      if(entry) {
        setInputValue(entry.total);
        setValue(config.name, entry.total);
        setBreakdown(entry.breakdown || []);
      }
    }
    // The form posts when updated_at changes, so toggling auto has to touch it the way choosing a unit does.
    setValue('updated_at', Date.now());
  }

  // On page load an already-automatic material field seeds its value + breakdown from the server-computed
  // window.material_auto_wastages, so the total and badges are right before any re-price. auto_breakdown only.
  useEffect(() => {
    if(!config.auto_breakdown || !isAuto) { return; }
    const entry = materialAutoEntry();
    if(!entry) { return; }

    setInputValue(entry.total);
    setValue(config.name, entry.total);
    setBreakdown(entry.breakdown || []);
  }, []);

  // Pricing a cost section recomputes THAT section's automatic fire adeti on the server (returned by the section
  // calculator and re-emitted by QuoteBuilderForm via emitEvent). While the field is in automatic mode -- read-only,
  // so nothing the user typed is at stake -- it takes the value ADDRESSED TO ITS OWN SECTION, so the count updates
  // without a reload. The event is a document-level broadcast and every nav section mounts its own field, so the
  // label filter is what stops re-pricing one section from overwriting the others (the same filter
  // total_price_in_pence_set uses in QuoteBuilderNavSection). isAuto already requires the v2 auto toggle, so a
  // manual field and every non-wastage field ignore it.
  useEffect(() => {
    // Which recompute event feeds this field: the nav-section wastage field takes the section total
    // (nav_section_wastage_recomputed { wastage_count }); the material fire adeti takes its own material total +
    // breakdown (material_wastage_recomputed { total, breakdown }) so it never bleeds the nav-section's value.
    const recomputeEvent = config.auto_recompute_event || 'nav_section_wastage_recomputed';

    // Apply the LATEST report (value + badges + the self-correct below); the debounce runs this once per burst.
    // A response<->form mismatch self-corrects here: the recompute carries the server's authoritative total; the
    // value this automatic field holds is what the section was last priced against, which a re-price race can leave
    // stale (a transient push of a lower total before a contributor's spoilage is back). When they differ, re-apply
    // the total AND bump updated_at so the section actually RE-PRICES against it -- applying the value alone updates
    // the display but never re-prices (the form only posts on updated_at / a user edit). The server recomputes the
    // count authoritatively too, so the price is right even if this pushed value is itself momentarily stale.
    const applyRecompute = (detail) => {
      const nextValue = detail.total != undefined ? detail.total : detail.wastage_count;
      if(detail.breakdown != undefined) { setBreakdown(detail.breakdown); }

      const isRealChange = String(nextValue) !== String(currentValueRef.current);
      setInputValue(nextValue);
      setValue(config.name, nextValue);

      if(config.auto_breakdown) {
        if(!isRealChange) {
          // Converged: the server returned the total we last re-priced against. Reset the loop guard.
          pushedAutoValuesRef.current.clear();
          cycleReportedRef.current = false;
        } else if(pushedAutoValuesRef.current.has(String(nextValue))) {
          // The server is alternating between totals this field has ALREADY re-priced against -- a non-converging
          // ping-pong (a multi-item section where MaterialAutoWastage and CoatingLaborMaterialReprice sized a column
          // differently was one cause). Re-posting again would loop forever, so STOP: show the latest total but do NOT
          // bump updated_at. The server re-prices authoritatively, so the persisted price stays right. Report it once
          // so a recurrence is caught in production, where this could not be reproduced locally.
          if(!cycleReportedRef.current && typeof trackEvent === 'function') {
            cycleReportedRef.current = true;
            trackEvent('material_wastage_reprice_cycle', {
              nav_section_label: navSection?.label,
              column_key: columnKey,
              values: Array.from(pushedAutoValuesRef.current).concat(String(nextValue))
            });
          }
        } else {
          // A new total we have not re-priced against yet: re-price (bump updated_at) and remember it.
          pushedAutoValuesRef.current.add(String(nextValue));
          setValue('updated_at', Date.now());
        }
      }
    };

    const onRecomputed = (event) => {
      if(event.detail.label != navSection?.label) { return; }
      // The material fire adeti event is per column; only take the one for THIS column, matched by column_key (the
      // title collides for carton_lamination). The nav-section wastage event carries no column_key, so its
      // undefined matches the field's undefined columnKey and still applies.
      if(config.auto_breakdown && (event.detail.column_key || '') !== (columnKey || '')) { return; }
      if(!isAuto) { return; }

      // Coalesce a burst of reports (see RECOMPUTE_SETTLE_DEBOUNCE_MS): keep only the latest and apply it once the
      // reports go quiet, so the input settles instead of flickering through each intermediate total.
      if(recomputeTimerRef.current) { clearTimeout(recomputeTimerRef.current); }
      const detail = event.detail;
      recomputeTimerRef.current = setTimeout(() => {
        recomputeTimerRef.current = null;
        applyRecompute(detail);
      }, RECOMPUTE_SETTLE_DEBOUNCE_MS);
    };
    document.addEventListener(recomputeEvent, onRecomputed);
    return () => {
      document.removeEventListener(recomputeEvent, onRecomputed);
      if(recomputeTimerRef.current) { clearTimeout(recomputeTimerRef.current); recomputeTimerRef.current = null; }
    };
  }, [isAuto, navSection?.label, columnKey]);

  const handleDelayedInputValue = (value) => {
    setValue(config.name, value);
    if(config.publish_on_change_key != undefined) {
      var event = new CustomEvent(config.publish_on_change_key, { "detail": value });
      document.dispatchEvent(event);
    }

    if((config.publish_item_on_change && config.publish_item_if_eval == undefined) || (config.publish_item_on_change && eval(config.publish_item_if_eval)) ) {
      requestPublishItemOnChange({ publish_item_key: config.publish_item_key });
    }
  }

  useEffect(() => {
    const timeOutId = setTimeout(() => handleDelayedInputValue(inputValue), 500);
    return () => clearTimeout(timeOutId);
  }, [inputValue]);

  // No naming convention is assumed: with a prefix the label is looked up, without one the value is
  // shown as it stands, so a list of already-readable units needs no translation keys at all.
  const unitLabel = (unitValue) => {
    return config.unit_translate_prefix != undefined ? t(config.unit_translate_prefix + unitValue) : unitValue;
  }

  const unitOptions = config.unit_values.map((unitValue) => {
    return { value: unitValue, label: unitLabel(unitValue) };
  });

  const handleUnitChange = (event) => {
    setValue(config.unit_name, event.target.value);
    // The form posts when updated_at changes, so choosing a unit has to touch it the way a select does.
    setValue('updated_at', Date.now());
  }

  // Queued on mount as well as on change, the way SelectFormField publishes, so subscribers still
  // receive the unit with the form's first post.
  useEffect(() => {
    if(config.unit_publish_item_key != undefined) {
      requestPublishItemOnChange({ publish_item_key: config.unit_publish_item_key });
    }
  }, [unit]);

  const inputElement = (
    <NumberWithStaticUnitsInput
      name={config.name}
      placeholder={config.placeholder}
      register={register}
      errors={errors}
      class_name={config.class_name}
      integerOnly={integerOnly}
      unitName={config.unit_name}
      unitValue={unit}
      unitOptions={unitOptions}
      unitAriaLabel={config.unit_label != undefined ? t(config.unit_label) : undefined}
      onUnitChange={handleUnitChange}
      unitDisabled={effectiveReadOnly || disabled}
      validators={config.validators != undefined ? config.validators : []}
      value={inputValue}
      onChange={event => setInputValue(event.target.value)}
      readOnly={effectiveReadOnly}
    />
  );

  // The auto/manual controls: while automatic, the ? tooltip explaining the value plus a "Düzenle" link back to
  // manual; while manual, a "use automatic" link. auto_controls_below drops them onto their own row (see below).
  const autoControls = isAuto ? (
    <>
      <span style={{ flexShrink: 0, marginLeft: config.auto_controls_below ? 0 : '6px' }}><Tooltip title={t(autoTooltip)} /></span>
      <a href="#" className="qb-attribute-edit-link" style={{ flexShrink: 0, whiteSpace: 'nowrap', marginLeft: '16px' }}
         onClick={(event) => { event.preventDefault(); setAuto(false); }}>
        <i className="fa fa-pencil-square-o" aria-hidden="true"></i> {t('quote_builder.edit')}
      </a>
    </>
  ) : (
    <a href="#" className="qb-attribute-edit-link" style={{ flexShrink: 0, whiteSpace: 'nowrap', marginLeft: config.auto_controls_below ? 0 : '16px' }}
       onClick={(event) => { event.preventDefault(); setAuto(true); }}>
      <i className="fa fa-magic" aria-hidden="true"></i> {t(config.auto_label)}
    </a>
  );

  // The reported-wastage breakdown badges, under the material fire adeti in automatic mode: one per contributing
  // section, each showing its contribution already converted into the material sheet -- "Baskı: 50 Tabaka" -- so the
  // badges add up to the total. (entry.amount is the reported count folded into the material sheet; the raw reported
  // count and its own sheet are not shown.) Falls back to the raw value + its unit only if no amount is present
  // (legacy data). Contributors that reported nothing are already dropped server-side; when NOTHING has been
  // reported yet (total 0, no badges), a single placeholder badge (config.auto_empty_label) explains the value
  // instead of leaving a bare, unexplained 0. auto_breakdown fields only; hidden in manual mode.
  const badgeStyle = { display: 'inline-block', padding: '2px 8px', borderRadius: '10px', background: '#f0f0f0', color: '#555', fontSize: '12px', whiteSpace: 'nowrap' };
  const materialUnit = (values && values[config.unit_name]) || (config.unit_values && config.unit_values[0]);
  const badgeText = (entry) => (
    entry.amount != undefined
      ? `${entry.label}: ${entry.amount} ${unitLabel(materialUnit)}`
      : `${entry.label}: ${entry.value} ${unitLabel(entry.unit)}`
  );
  const badges = breakdown.length > 0
    ? breakdown.map((entry, index) => (
        <span key={index} style={badgeStyle}>{badgeText(entry)}</span>
      ))
    : (config.auto_empty_label != undefined ? [<span key="empty" style={badgeStyle}>{t(config.auto_empty_label)}</span>] : []);
  const badgeRow = (config.auto_breakdown && isAuto && badges.length > 0) ? (
    <div style={{ marginTop: '6px', display: 'flex', flexWrap: 'wrap', gap: '4px', justifyContent: 'flex-end' }}>
      {badges}
    </div>
  ) : null;

  return (
    <tr>
      <td>
        {t(config.label)} { config.tooltip_text != undefined && <Tooltip title={t(config.tooltip_text)} /> }
        {config.helper_text != undefined && (
          <><br /><small className="form-text text-muted" dangerouslySetInnerHTML={{ __html: t(config.helper_text) }} /></>
        )}
      </td>
      <td align="right">
        {hasAutoToggle ? (
          // Mirrors the app's select + edit-link rows (e.g. the material picker): a 265px box, right-aligned like
          // the .general-frm-element selects above and below, so the input's left edge lines up with them.
          config.auto_controls_below ? (
            // The tooltip + edit/auto controls sit on their OWN row below the input, matching the material price
            // selector above (whose Düzenle / Hepsini göster links sit under the select). The td is right-aligned,
            // so the controls row lands under the input.
            <>
              <div style={{ display: 'inline-flex', alignItems: 'center', width: '265px' }}>
                <div className="wastage-auto-value" style={{ flex: '1 1 auto', minWidth: 0, textAlign: 'left' }}>{inputElement}</div>
              </div>
              {badgeRow}
              <div style={{ marginTop: '4px' }}>{autoControls}</div>
            </>
          ) : (
            <div style={{ display: 'inline-flex', alignItems: 'center', width: '265px' }}>
              <div className="wastage-auto-value" style={{ flex: '1 1 auto', minWidth: 0, textAlign: 'left' }}>{inputElement}</div>
              {autoControls}
            </div>
          )
        ) : inputElement}
      </td>
    </tr>
  )
}

export default DelayedNumberWithStaticUnitsFormField;
